/**
 * useQuestJourney — one quest's journey-map data for a Drop surface slot.
 *
 * The Road column (anchor rttf-drop-hub.html:1967, kind JOURNEY) shows a
 * single configured quest as the winding step ladder. The ladder itself is
 * `SegmentJourneyPath`, and the steps come from the SAME `deriveSteps` the
 * play page uses — one derivation, two mounts.
 *
 * Fetches the user's quest configs unfiltered (no single-quest user endpoint
 * exists — QuestBlock records the same constraint) plus the progression for
 * the one quest asked about.
 */
import { useSelector } from "react-redux";
import { IStore } from "@/redux/defaultStore";
import { useGetQuestConfigsForUser } from "@/hooks/useGetQuestConfigsForUser";
import { useGetQuestProgressionForUser } from "@/hooks/useGetQuestProgressionForUser";
import { deriveSteps, DerivedStep } from "@/hooks/useOnboardingCards";
import { QuestConfigResponse } from "@devour/client";

export interface QuestJourney {
    quest?: QuestConfigResponse;
    steps: DerivedStep[];
    /** True while either query is still settling for an authenticated user. */
    loading: boolean;
}

export function useQuestJourney(questConfigId: string | undefined): QuestJourney {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);

    const { data: quests } = useGetQuestConfigsForUser(fullToken, {});
    const quest = questConfigId
        ? quests?.find(q => q.id === questConfigId)
        : undefined;

    const { data: progressions } = useGetQuestProgressionForUser(
        fullToken,
        quest?.id ? [quest.id] : [],
    );
    const progression = progressions?.find(p => p.questConfigId === quest?.id);

    return {
        quest,
        steps: quest ? deriveSteps(quest, progression) : [],
        loading: Boolean(fullToken) && (quests === undefined || Boolean(quest) && progressions === undefined),
    };
}
